/**
 * Pure math behind the three Pilots calculators, kept out of the components so
 * every number on screen can be unit tested and reproduced by hand.
 *
 * Inputs arrive from form fields, so anything that is not a finite,
 * non-negative number counts as 0 instead of poisoning the totals with NaN.
 */

export const WORK_HOURS_PER_YEAR = 2080;
export const WORK_HOURS_PER_WEEK = 40;
const WORK_HOURS_PER_MONTH = WORK_HOURS_PER_YEAR / 12;

export function toAmount(value) {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * What the pilot costs us in engineering time.
 *
 * @param {{ annualSalary: number, overheadPct: number, hoursPerWeek: number,
 *   weeks: number, developers: number }} inputs
 */
export function calculateDevCost(inputs) {
  const annualSalary = toAmount(inputs.annualSalary);
  const overheadPct = toAmount(inputs.overheadPct);
  const hoursPerWeek = toAmount(inputs.hoursPerWeek);
  const weeks = toAmount(inputs.weeks);
  const developers = toAmount(inputs.developers);

  const hourlyRate =
    (annualSalary * (1 + overheadPct / 100)) / WORK_HOURS_PER_YEAR;
  const weeklyHours = hoursPerWeek * developers;
  const totalHours = weeklyHours * weeks;

  return {
    hourlyRate,
    totalHours,
    weeklyCost: hourlyRate * weeklyHours,
    totalCost: hourlyRate * totalHours,
    shareOfFullTime: hoursPerWeek / WORK_HOURS_PER_WEEK,
  };
}

/**
 * What running Addy on the pilot's loans costs us.
 *
 * @param {{ costPerRun: number, loanOfficers: number,
 *   loansPerOfficer: number, runsPerLoan: number, weeks: number }} inputs
 */
export function calculateLoanRunCost(inputs) {
  const costPerRun = toAmount(inputs.costPerRun);
  const totalLoans = toAmount(inputs.loanOfficers) * toAmount(inputs.loansPerOfficer);
  const totalRuns = totalLoans * toAmount(inputs.runsPerLoan);
  const totalCost = totalRuns * costPerRun;
  const weeks = toAmount(inputs.weeks);

  return {
    totalLoans,
    totalRuns,
    totalCost,
    costPerLoan: totalLoans ? totalCost / totalLoans : 0,
    weeklyCost: weeks ? totalCost / weeks : 0,
  };
}

/**
 * What the client pays us per month under either pricing model.
 *
 * @param {{ pricingModel: "perLoan"|"perSeat", loansPerMonth: number,
 *   pricePerLoan: number, pricePerSeat: number, seats: number }} inputs
 */
export function calculateAddyMonthlyCost(inputs) {
  if (inputs.pricingModel === "perSeat") {
    return toAmount(inputs.seats) * toAmount(inputs.pricePerSeat);
  }

  return toAmount(inputs.loansPerMonth) * toAmount(inputs.pricePerLoan);
}

/**
 * The client's return on Addy: staff time given back, what that time is
 * worth, and how it compares with what they pay us.
 *
 * A task's "with Addy" minutes are capped at its "today" minutes, so a typo
 * cannot turn saved time negative.
 *
 * @param {{ loansPerMonth: number, hourlyCost: number,
 *   turnTimeDaysSaved: number, pricingModel: "perLoan"|"perSeat",
 *   pricePerLoan: number, pricePerSeat: number, seats: number,
 *   tasks: Array<{ key: string, minutesToday: number,
 *     minutesWithAddy: number }> }} inputs
 */
export function calculateRoi(inputs) {
  const loansPerMonth = toAmount(inputs.loansPerMonth);
  const hourlyCost = toAmount(inputs.hourlyCost);
  const tasks = (inputs.tasks || []).map((task) => {
    const minutesToday = toAmount(task.minutesToday);
    const minutesWithAddy = Math.min(toAmount(task.minutesWithAddy), minutesToday);

    return {
      ...task,
      minutesToday,
      minutesWithAddy,
      minutesSaved: minutesToday - minutesWithAddy,
    };
  });
  const sum = (key) => tasks.reduce((total, task) => total + task[key], 0);

  const minutesTodayPerLoan = sum("minutesToday");
  const minutesWithAddyPerLoan = sum("minutesWithAddy");
  const minutesSavedPerLoan = sum("minutesSaved");
  const monthlyHoursSaved = (loansPerMonth * minutesSavedPerLoan) / 60;
  const monthlyLaborSavings = monthlyHoursSaved * hourlyCost;
  const monthlyAddyCost = calculateAddyMonthlyCost(inputs);
  const monthlyNetSavings = monthlyLaborSavings - monthlyAddyCost;

  return {
    tasks,
    minutesTodayPerLoan,
    minutesWithAddyPerLoan,
    minutesSavedPerLoan,
    timeReduction: minutesTodayPerLoan
      ? minutesSavedPerLoan / minutesTodayPerLoan
      : 0,
    savingsPerLoan: (minutesSavedPerLoan / 60) * hourlyCost,
    monthlyHoursSaved,
    annualHoursSaved: monthlyHoursSaved * 12,
    fteFreed: monthlyHoursSaved / WORK_HOURS_PER_MONTH,
    monthlyLaborSavings,
    annualLaborSavings: monthlyLaborSavings * 12,
    monthlyAddyCost,
    annualAddyCost: monthlyAddyCost * 12,
    monthlyNetSavings,
    annualNetSavings: monthlyNetSavings * 12,
    roiMultiple: monthlyAddyCost ? monthlyLaborSavings / monthlyAddyCost : null,
    turnTimeDaysSaved: toAmount(inputs.turnTimeDaysSaved),
  };
}

/**
 * Running totals year by year, assuming today's volume and prices hold.
 *
 * @param {ReturnType<typeof calculateRoi>} result
 * @param {number} [years]
 * @returns {Array<{ year: number, label: string, savings: number,
 *   addyCost: number, net: number }>}
 */
export function buildRoiForecast(result, years = 3) {
  return Array.from({ length: years }, (_, index) => {
    const year = index + 1;
    const savings = result.annualLaborSavings * year;
    const addyCost = result.annualAddyCost * year;

    return { year, label: `Year ${year}`, savings, addyCost, net: savings - addyCost };
  });
}
